import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import type { PartnerSignatureInput } from "../../shared/types";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { partnerApi, PartnerApiError } from "../lib/api";
import { agreementClauses } from "../lib/agreement";
import { SignModal } from "../components/SignModal";
import { PortalMark } from "../components/Logo";
import { useGoBack } from "../../shared/phone/history";
import { scrollToTop, useNative } from "../../shared/phone/media";
import { NativeFrame } from "../components/phone/Frame";
import { OnboardingNative, type PayKind, type StepKey } from "./OnboardingNative";

const STEP_LABEL: Record<StepKey, string> = {
  about: "About you",
  agreement: "Agreement",
  submitted: "Review",
  tax: "Tax form",
  payout: "Payout",
  done: "Done",
};

const PAY_LABEL: Record<PayKind, string> = {
  paypal: "PayPal email",
  bank: "Account number",
  wise: "Wise email or account",
};

function today(): string {
  return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function Ink({ sig }: { sig: PartnerSignatureInput }) {
  if (sig.kind === "drawn" && sig.dataUrl) return <img src={sig.dataUrl} alt="Signature" />;
  return <span className="sig-ink">{sig.name}</span>;
}

/**
 * The onboarding wizard for one program: About you (application), e-sign the
 * agreement, application sent, tax form (W-9 / W-8BEN), payout method, and done.
 * Every step persists to the real API; the agreement signature is captured by
 * the sign modal and stored server side with a server-derived audit.
 */
export function OnboardingView() {
  const { programId = "" } = useParams();
  const { session, applyMembership, selectProgram, currentMembership } = usePartnerAuth();
  const navigate = useNavigate();
  const native = useNative();
  const goBack = useGoBack();

  const membership = session?.programs.find((p) => p.programId === programId) ?? null;

  // The step list is computed ONCE for this enrollment and frozen: onboarding
  // steps persist and update the membership as we go, so recomputing would drop a
  // just-satisfied step and shift the indices mid-wizard. A step already
  // satisfied at entry is skipped.
  const [flow] = useState<StepKey[]>(() => {
    if (!membership) return ["done"];
    if (membership.needsResign) return ["agreement", "done"];
    const steps: StepKey[] = ["about", "agreement", "submitted"];
    if (!membership.docStatus.tax) steps.push("tax");
    if (!membership.docStatus.payoutMethod) steps.push("payout");
    steps.push("done");
    return steps;
  });

  const partnerName = session ? `${session.partner.firstName} ${session.partner.lastName}`.trim() : "";
  const product = membership?.productName ?? "the program";

  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Application
  const [audienceSize, setAudienceSize] = useState("");
  const [mainChannel, setMainChannel] = useState("");
  const [promoPlan, setPromoPlan] = useState("");

  // Agreement
  const [agreementSig, setAgreementSig] = useState<PartnerSignatureInput | null>(
    membership?.agreementSigned && !membership.needsResign
      ? { kind: "typed", name: partnerName }
      : null,
  );

  // Tax
  const [taxForm, setTaxForm] = useState<"W-9" | "W-8BEN">("W-9");
  const [legalName, setLegalName] = useState(partnerName);
  const [country, setCountry] = useState("United States");
  const [taxClass, setTaxClass] = useState("Individual / sole proprietor");
  const [taxAddress, setTaxAddress] = useState("");
  const [taxpayerId, setTaxpayerId] = useState("");
  const [certified, setCertified] = useState(false);
  const [taxSig, setTaxSig] = useState<PartnerSignatureInput | null>(null);

  // Payout
  const [payKind, setPayKind] = useState<PayKind>("paypal");
  const [payValue, setPayValue] = useState("");

  // Sign modal
  const [signOpen, setSignOpen] = useState(false);
  const [signTarget, setSignTarget] = useState<"agreement" | "tax">("agreement");

  if (!session) return <Navigate to="/signup" replace />;
  if (!membership) return <Navigate to="/programs" replace />;

  const key = flow[idx];
  const isLast = idx === flow.length - 1;
  const usPerson = taxForm === "W-9";

  function openSign(target: "agreement" | "tax") {
    setSignTarget(target);
    setSignOpen(true);
  }

  async function onAdopt(sig: PartnerSignatureInput) {
    setSignOpen(false);
    if (signTarget === "tax") {
      setTaxSig(sig);
      return;
    }
    // Agreement: persist the signature to the server (real audit derived there).
    setBusy(true);
    setError(null);
    try {
      const { membership: next } = await partnerApi.signAgreement({
        programId,
        signature: sig,
      });
      applyMembership(next);
      setAgreementSig(sig);
    } catch (err) {
      setError(err instanceof PartnerApiError ? err.message : "Could not save your signature.");
    } finally {
      setBusy(false);
    }
  }

  function canContinue(): boolean {
    if (busy) return false;
    if (key === "agreement") return agreementSig !== null;
    if (key === "tax") return certified && taxSig !== null && legalName.trim().length > 0;
    if (key === "payout") return payValue.trim().length > 0;
    return true;
  }

  function hint(): string {
    if (key === "agreement") return agreementSig ? "Signed" : "Sign the agreement to continue";
    if (key === "tax") return certified && taxSig ? "Signed and certified" : "Certify and sign to continue";
    return "";
  }

  async function persistStep(): Promise<void> {
    if (key === "about") {
      await partnerApi
        .saveApplication({ programId, audienceSize, mainChannel, promoPlan })
        .then((r) => applyMembership(r.membership));
    } else if (key === "tax") {
      await partnerApi
        .submitTax({
          programId,
          form: taxForm,
          legalName,
          country,
          taxClassification: usPerson ? taxClass : null,
          address: taxAddress || null,
          taxpayerId: taxpayerId || null,
          certified,
        })
        .then((r) => applyMembership(r.membership));
    } else if (key === "payout") {
      const fields: Record<string, string> =
        payKind === "paypal" ? { email: payValue } : { account: payValue };
      const method = payKind === "paypal" ? "PayPal" : payKind === "wise" ? "Wise" : "Bank transfer";
      await partnerApi
        .savePayoutMethod({ programId, method, fields })
        .then((r) => applyMembership(r.membership));
    }
  }

  async function advance() {
    if (!canContinue()) return;
    setBusy(true);
    setError(null);
    try {
      await persistStep();
      if (isLast) {
        selectProgram(programId);
        navigate("/home", { replace: true });
        return;
      }
      setIdx((i) => i + 1);
      scrollToTop();
    } catch (err) {
      setError(err instanceof PartnerApiError ? err.message : "Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function back() {
    if (idx > 0) {
      setIdx((i) => i - 1);
      scrollToTop();
    }
  }

  if (native) {
    // Back from the first step: to Home inside the app, or to the list when there is one to choose from.
    const parent = currentMembership ? "/home" : "/programs";
    const leave = parent === "/home" || session.programs.length > 1 ? () => goBack(parent) : null;
    return (
      <NativeFrame nav={false}>
        <OnboardingNative
          o={{
            membership,
            product,
            partnerName,
            today: today(),
            flow,
            idx,
            busy,
            error,
            canContinue: canContinue(),
            advance: () => void advance(),
            stepBack: back,
            leave,
            audienceSize,
            setAudienceSize,
            mainChannel,
            setMainChannel,
            promoPlan,
            setPromoPlan,
            agreementSig,
            taxForm,
            setTaxForm,
            legalName,
            setLegalName,
            country,
            setCountry,
            taxClass,
            setTaxClass,
            taxAddress,
            setTaxAddress,
            taxpayerId,
            setTaxpayerId,
            certified,
            setCertified,
            taxSig,
            payKind,
            setPayKind: (k) => {
              setPayKind(k);
              setPayValue("");
            },
            payValue,
            setPayValue,
            signOpen,
            signTarget,
            openSign,
            closeSign: () => setSignOpen(false),
            onAdopt: (sig) => void onAdopt(sig),
          }}
        />
      </NativeFrame>
    );
  }

  return (
    <div className="onboard">
      <div className="ob-top">
        <div className="brand">
          <PortalMark />
          <span className="ob-prod">{product}</span> <span className="l">Partners</span>
        </div>
        <div className="ob-steps">
          {flow
            .filter((k) => k !== "done")
            .map((k) => {
              const at = flow.indexOf(k);
              const cls = at < idx ? "done" : k === key ? "on" : "";
              return (
                <span key={k} className={`ob-dot ${cls}`}>
                  {STEP_LABEL[k]}
                </span>
              );
            })}
        </div>
      </div>

      <div className="ob-scroll">
        {error && (
          <div className="ob-error" role="alert">
            {error}
          </div>
        )}

        {key === "about" && (
          <div className="wpane active">
            <div className="ob-card">
              <h2>Tell {product} about you</h2>
              <p className="ob-sub">
                A quick application. {product} reviews this before your link goes live.
              </p>
              <div className="frow">
                <div className="field">
                  <label htmlFor="ob-audience">Audience size</label>
                  <input
                    id="ob-audience"
                    className="inp"
                    placeholder="e.g. 12,000"
                    value={audienceSize}
                    onChange={(e) => setAudienceSize(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="ob-channel">Main channel</label>
                  <input
                    id="ob-channel"
                    className="inp"
                    placeholder="Instagram, email, blog"
                    value={mainChannel}
                    onChange={(e) => setMainChannel(e.target.value)}
                  />
                </div>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="ob-promo">How will you promote it?</label>
                <textarea
                  id="ob-promo"
                  className="inp"
                  rows={3}
                  placeholder="A sentence or two on your plan"
                  value={promoPlan}
                  onChange={(e) => setPromoPlan(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {key === "agreement" && (
          <div className="wpane active">
            <div className="docpage">
              <div className="dh">{product} Partner Agreement</div>
              <div className="dsub">Between {product} and the Partner</div>
              {agreementClauses(product).map((c) => (
                <div key={c.n}>
                  <h5>
                    {c.n}. {c.title}
                  </h5>
                  <p>{c.body}</p>
                </div>
              ))}
              <div className="sigblock">
                <div className="sigcol">
                  <div
                    className={`sigfield${agreementSig ? " done" : ""}`}
                    onClick={() => !agreementSig && openSign("agreement")}
                    role="button"
                    tabIndex={0}
                  >
                    {agreementSig ? (
                      <Ink sig={agreementSig} />
                    ) : (
                      <>
                        <span className="sigtag">Sign</span>
                        <span className="sigph">Sign here</span>
                      </>
                    )}
                  </div>
                  <div className="sigcap">Partner signature</div>
                </div>
                <div className="sigcol">
                  <div className="datebox">{today()}</div>
                  <div className="sigcap">Date</div>
                </div>
              </div>
            </div>
            <div className="doc-audit">
              Secured e-signature by Flashadz. Your IP and time are recorded on completion.
            </div>
          </div>
        )}

        {key === "submitted" && (
          <div className="wpane active">
            <div className="ob-card center">
              <div className="ob-ic">&#10003;</div>
              <h2>Application sent</h2>
              <p>
                {product} is reviewing you and will email when you are approved. While you wait, set
                up how you get paid.
              </p>
            </div>
          </div>
        )}

        {key === "tax" && (
          <div className="wpane active">
            <div className="ob-card">
              <h2>Your tax form</h2>
              <p className="ob-sub">
                {product} needs this to pay you and report at year end. We fill the official form
                from your answers.
              </p>
              <div className="seg" style={{ marginBottom: 16 }}>
                <button
                  type="button"
                  className={usPerson ? "on" : ""}
                  onClick={() => setTaxForm("W-9")}
                >
                  W-9 (in the US)
                </button>
                <button
                  type="button"
                  className={!usPerson ? "on" : ""}
                  onClick={() => setTaxForm("W-8BEN")}
                >
                  W-8BEN (outside the US)
                </button>
              </div>
              <div className="frow">
                <div className="field">
                  <label htmlFor="ob-legal">Legal name</label>
                  <input id="ob-legal" className="inp" value={legalName} onChange={(e) => setLegalName(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="ob-country">{usPerson ? "Country" : "Country of citizenship"}</label>
                  <input id="ob-country" className="inp" value={country} onChange={(e) => setCountry(e.target.value)} />
                </div>
              </div>
              {usPerson && (
                <div className="field">
                  <label htmlFor="ob-taxclass">Tax classification</label>
                  <input id="ob-taxclass" className="inp" value={taxClass} onChange={(e) => setTaxClass(e.target.value)} />
                </div>
              )}
              <div className="field">
                <label htmlFor="ob-address">Address</label>
                <input
                  id="ob-address"
                  className="inp"
                  placeholder="Street, city, state, ZIP"
                  value={taxAddress}
                  onChange={(e) => setTaxAddress(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="ob-taxid">{usPerson ? "Taxpayer ID (SSN or EIN)" : "Foreign tax ID"}</label>
                <input
                  id="ob-taxid"
                  className="inp"
                  placeholder={usPerson ? "000-00-0000" : "Tax ID"}
                  value={taxpayerId}
                  onChange={(e) => setTaxpayerId(e.target.value)}
                />
              </div>
              <label className="certify">
                <input
                  type="checkbox"
                  checked={certified}
                  onChange={(e) => setCertified(e.target.checked)}
                />
                Under penalties of perjury, I certify the information above is true and correct.
              </label>
              <div className="tax-sign">
                <div
                  className={`sigfield${taxSig ? " done" : ""}`}
                  onClick={() => !taxSig && openSign("tax")}
                  role="button"
                  tabIndex={0}
                >
                  {taxSig ? (
                    <Ink sig={taxSig} />
                  ) : (
                    <>
                      <span className="sigtag">Sign</span>
                      <span className="sigph">Sign here</span>
                    </>
                  )}
                </div>
                <span className="tax-date">Dated {today()}</span>
              </div>
              <div className="ob-note">
                <span className="oi">&#9670;</span>We generate your signed form and share it with{" "}
                {product}. No upload needed.
              </div>
            </div>
          </div>
        )}

        {key === "payout" && (
          <div className="wpane active">
            <div className="ob-card">
              <h2>How you get paid</h2>
              <p className="ob-sub">{product} sends your rewards here. Change it any time.</p>
              <div className="seg" style={{ marginBottom: 16 }}>
                {(["paypal", "bank", "wise"] as PayKind[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={payKind === k ? "on" : ""}
                    onClick={() => {
                      setPayKind(k);
                      setPayValue("");
                    }}
                  >
                    {k === "paypal" ? "PayPal" : k === "bank" ? "Bank" : "Wise"}
                  </button>
                ))}
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="ob-payval">{PAY_LABEL[payKind]}</label>
                <input
                  id="ob-payval"
                  className="inp"
                  value={payValue}
                  onChange={(e) => setPayValue(e.target.value)}
                  placeholder={payKind === "bank" ? "Bank account number" : "name@email.com"}
                />
              </div>
              <div className="ob-note">
                <span className="oi">&#9670;</span>
                {product} pays you directly. Flashadz records what you are owed, it never holds your
                money.
              </div>
            </div>
          </div>
        )}

        {key === "done" && (
          <div className="wpane active">
            <div className="ob-card center">
              <div className="ob-ic">&#9733;</div>
              <h2>You are all set</h2>
              <p>
                Start sharing your link now. Your rewards unlock once {product} approves you.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="ob-foot">
        <button
          className="btn ghost"
          type="button"
          onClick={back}
          style={{ visibility: idx > 0 ? "visible" : "hidden" }}
        >
          Back
        </button>
        <span className="ob-hint">{hint()}</span>
        <button className="btn" type="button" onClick={advance} disabled={!canContinue()}>
          {isLast ? "Enter dashboard" : "Continue"}
        </button>
      </div>

      {signOpen && (
        <SignModal
          key={signTarget}
          defaultName={partnerName}
          onClose={() => setSignOpen(false)}
          onAdopt={onAdopt}
        />
      )}
    </div>
  );
}
